import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Mail } from "lucide-react";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { DEV_MODE, getCaptchaToken, getCaptchaHeaders, handleDevModeEmailSubmit } from "../utils/devMode";
import { useRoleBasedApi } from '../hooks/useRoleBasedApi';
import { apiService } from "../../services/ApiService";

// Add Turnstile type declaration
declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
    };
  }
}

const NewsletterSignup: React.FC = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "rate_limit" | "validation">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string>("");
  const { post } = useRoleBasedApi();

  useEffect(() => {
    // Only load Turnstile script if not in dev mode
    if (!DEV_MODE.isEnabled) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, []);

  useEffect(() => {
    // Only render Turnstile widget if not in dev mode
    if (!DEV_MODE.isEnabled && window.turnstile) {
      const widgetId = window.turnstile.render("#turnstile-container", {
        sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          setTurnstileToken(token);
        },
        "expired-callback": () => {
          setTurnstileToken("");
          window.turnstile.reset(turnstileWidgetId);
        },
      });
      setTurnstileWidgetId(widgetId);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!DEV_MODE.isEnabled && !turnstileToken) {
      setStatus("validation");
      setErrorMsg("Please complete the security check.");
      return;
    }

    setStatus("loading");
    setErrorMsg("");
    
    // Handle dev mode submission
    const devModeResult = await handleDevModeEmailSubmit("subscribe", { email }, {
      onSuccess: () => {
        setStatus("success");
        setEmail("");
        if (!DEV_MODE.isEnabled) {
          setTurnstileToken("");
          window.turnstile.reset(turnstileWidgetId);
        }
      },
      onRateLimit: () => {
        setStatus("rate_limit");
        setErrorMsg("Too many requests. Please try again later.");
      },
      onValidation: (message) => {
        setStatus("validation");
        setErrorMsg(message);
      }
    });

    if (DEV_MODE.isEnabled && devModeResult) {
      return;
    }
    
    // Use API abstraction
    try {
      await post("/subscribe", { email }, { isPublic: true, headers: getCaptchaHeaders(turnstileToken) });
      setStatus("success");
      setEmail("");
      if (!DEV_MODE.isEnabled) {
        setTurnstileToken("");
        window.turnstile.reset(turnstileWidgetId);
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        setStatus("rate_limit");
        setErrorMsg("Too many requests. Please try again later.");
      } else if (error.response?.status === 422) {
        setStatus("validation");
        setErrorMsg(error.response?.data?.message || "Invalid email address.");
      } else {
        setStatus("error");
        setErrorMsg("Something went wrong. Please try again later.");
      }
    }
  };

  return (
    <div className="bg-sauna-navy text-white rounded-2xl p-8 md:p-12 shadow-xl">
      <div className="max-w-2xl mx-auto text-center">
        <Mail className="h-12 w-12 text-sauna-orange mx-auto mb-4" />
        <h3 className="text-2xl md:text-3xl font-bold mb-4">Stay Updated</h3>
        <p className="text-sauna-beige/80 mb-8">
          Join our newsletter for exclusive updates, wellness tips, and early access to new features.
        </p>

        {/* Dev Mode Indicator - Only visible in development */}
        {DEV_MODE.isEnabled && (
          <div className="flex items-center justify-center gap-2 mb-4 text-sm">
            <div className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-xs">
              Development Mode Active
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-md mx-auto">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20"
            required
          />
          
          {/* Turnstile Widget Container - Only visible when not in dev mode */}
          {!DEV_MODE.isEnabled && <div id="turnstile-container" className="flex justify-center my-2" />}
          
          <Button
            type="submit"
            className="bg-sauna-orange hover:bg-sauna-orange/90 text-white whitespace-nowrap"
            disabled={status === "loading" || (!DEV_MODE.isEnabled && !turnstileToken)}
          >
            {status === "loading" ? "Subscribing..." : "Subscribe"}
          </Button>
        </form>

        {status === "success" && (
          <p className="text-green-400 mt-4">Thanks for subscribing! Welcome to the SaunaDip community.</p>
        )}
        {status === "rate_limit" && (
          <p className="text-red-400 mt-4">Too many requests. Please try again later.</p>
        )}
        {status === "validation" && (
          <p className="text-red-400 mt-4">{errorMsg}</p>
        )}
        {status === "error" && (
          <p className="text-red-400 mt-4">{errorMsg}</p>
        )}
      </div>
    </div>
  );
};

export default NewsletterSignup; 